import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Loader2, Phone, Briefcase, Building2, Crown, Trash2,
  Calendar, MapPin, Shirt, GraduationCap, School, AlertCircle,
} from 'lucide-react';
import { authService } from '../services';

const norm      = r => (r || '').toLowerCase();
const roleLabel = r => { const n = norm(r); return n === 'admin' ? 'Admin' : n === 'parent' ? 'Parent' : 'Child'; };
const sexEmoji  = s => s === 'male' || s === 'm' ? '👨' : s === 'female' || s === 'f' ? '👩' : '🧑';
const isParentR = r => ['admin', 'parent'].includes(norm(r));

function formatDob(dob) {
  if (!dob) return null;
  try { return new Date(dob).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return dob; }
}

function ProfileField({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon size={15} className="text-slate-500" />
      </div>
      <div>
        <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-sm font-semibold text-slate-800">{value}</p>
      </div>
    </div>
  );
}

// ─── UserDetailModal ──────────────────────────────────────────────────────────
export default function UserDetailModal({ member, isAdmin, isCurrentUser, onRemove, onClose }) {
  const [deleteStep, setDeleteStep] = useState(0); // 0=idle 1=step1 2=step2 3=step3
  const [removing,   setRemoving]   = useState(false);
  const [profile,    setProfile]    = useState(null);
  // 'loading' | 'ok' | 'forbidden' | 'not_found' | 'error'
  const [loadState,  setLoadState]  = useState('loading');

  const displayName = member.display_name || member.name
    || `${roleLabel(member.role)} (${member.uid?.slice(0, 6)}…)`;
  const parentRole  = isParentR(member.role);
  const isChild     = norm(member.role) === 'child';
  const canDelete   = isAdmin && !isCurrentUser;

  useEffect(() => {
    if (!member.uid) { setLoadState('error'); return; }
    authService.getProfileByUid(member.uid)
      .then(data => { setProfile(data); setLoadState('ok'); })
      .catch(err => {
        const status = err?.response?.status;
        if (status === 403) setLoadState('forbidden');
        else if (status === 404) setLoadState('not_found');
        else setLoadState('error'); // network / route not ready yet → show basic info
      });
  }, [member.uid]);

  const resolvedName = profile?.name || displayName;

  const handleFinalRemove = async () => {
    setRemoving(true);
    try { await onRemove(member); onClose(); }
    finally { setRemoving(false); setDeleteStep(0); }
  };

  // ── Helpers for empty-state check ──────────────────────────────────────────
  const childFields  = profile ? (profile.tshirt_size || profile.school_name || profile.grade) : null;
  const parentFields = profile ? (profile.career || profile.company_name) : null;
  const commonFields = profile ? (profile.phone || profile.dob || profile.address) : null;
  const hasAnyField  = !!(commonFields || (isChild ? childFields : parentFields));

  return (
    <>
      {/* ── Base: User Detail Modal ─────────────────────────────────────────── */}
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      >
        <motion.div
          data-testid="user-detail-modal"
          className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
          initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 16 }}
          transition={{ duration: 0.2 }}
        >
          {/* Colour bar */}
          <div className={`h-1.5 ${parentRole
            ? 'bg-gradient-to-r from-sky-400 to-blue-500'
            : 'bg-gradient-to-r from-violet-400 to-purple-500'}`} />

          {/* Header */}
          <div className="px-8 pt-7 pb-5 border-b border-slate-100">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl flex-shrink-0
                  ${parentRole ? 'bg-sky-100' : 'bg-violet-100'}`}>
                  {member.avatar_url
                    ? <img src={member.avatar_url} alt={resolvedName} className="w-full h-full rounded-2xl object-cover" />
                    : <span>{sexEmoji(profile?.sex || member.sex)}</span>}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 font-outfit">{resolvedName}</h3>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full border
                      ${parentRole
                        ? 'bg-sky-50 text-sky-600 border-sky-100'
                        : 'bg-violet-50 text-violet-600 border-violet-100'}`}>
                      {roleLabel(member.role)}
                    </span>
                    {norm(member.role) === 'admin' && (
                      <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5">
                        <Crown size={10} /> Admin
                      </span>
                    )}
                    {isCurrentUser && (
                      <span className="text-xs text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">You</span>
                    )}
                  </div>
                </div>
              </div>
              <button
                data-testid="user-detail-close-btn"
                onClick={onClose}
                className="p-2 hover:bg-slate-100 rounded-xl text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-8 py-6 space-y-4">

            {/* Loading */}
            {loadState === 'loading' && (
              <div className="flex items-center justify-center py-8 gap-2 text-slate-400">
                <Loader2 size={18} className="animate-spin" />
                <span className="text-sm">Loading profile…</span>
              </div>
            )}

            {/* 403 — different family */}
            {loadState === 'forbidden' && (
              <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-2xl text-amber-700 text-sm">
                <AlertCircle size={15} className="flex-shrink-0" />
                Cannot view this profile — family access restricted.
              </div>
            )}

            {/* 404 — never signed in / profile not initialized */}
            {loadState === 'not_found' && (
              <div className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-2xl text-slate-500 text-sm">
                <AlertCircle size={15} className="flex-shrink-0" />
                Profile not set up yet — this member hasn't signed in to the portal.
              </div>
            )}

            {/* Full profile (ok) */}
            {loadState === 'ok' && profile && (
              <>
                <ProfileField icon={Phone}    label="Phone"        value={profile.phone} />
                <ProfileField icon={Calendar} label="Date of Birth" value={formatDob(profile.dob)} />
                <ProfileField icon={MapPin}   label="Address"      value={profile.address} />

                {isChild && <>
                  <ProfileField icon={Shirt}         label="T-Shirt Size" value={profile.tshirt_size} />
                  <ProfileField icon={School}        label="School"       value={profile.school_name} />
                  <ProfileField icon={GraduationCap} label="Grade"        value={profile.grade} />
                </>}

                {!isChild && <>
                  <ProfileField icon={Briefcase} label="Career"    value={profile.career} />
                  <ProfileField icon={Building2} label="Workplace" value={profile.company_name} />
                </>}

                {!hasAnyField && (
                  <p className="text-sm text-slate-400 text-center py-2 italic">No additional details available.</p>
                )}
              </>
            )}

            {/* Error / APIGW route not ready — fall back to basic member data */}
            {loadState === 'error' && (
              <>
                <ProfileField icon={Phone}     label="Phone"     value={member.phone} />
                <ProfileField icon={Briefcase} label="Career"    value={member.career} />
                <ProfileField icon={Building2} label="Workplace" value={member.workplace || member.company} />
                {!member.phone && !member.career && !member.workplace && !member.company && (
                  <p className="text-sm text-slate-400 text-center py-2 italic">No additional details available.</p>
                )}
              </>
            )}
          </div>

          {/* Footer — delete action (admin only, not self) */}
          {canDelete && (
            <div className="px-8 pb-7 pt-3 border-t border-slate-100">
              <button
                data-testid="delete-member-btn"
                onClick={() => setDeleteStep(1)}
                className="flex items-center gap-2 px-4 py-2.5 border border-red-200 text-red-500 hover:bg-red-50 rounded-2xl text-sm font-semibold transition-colors"
              >
                <Trash2 size={14} /> Delete Member
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>

      {/* ── Delete Step 1: "Remove Member?" ────────────────────────────────── */}
      <AnimatePresence>
        {deleteStep >= 1 && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/25 backdrop-blur-[2px]"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <motion.div
              data-testid="delete-step1-modal"
              className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8"
              initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.18 }}
            >
              <h3 className="text-xl font-bold text-slate-900 font-outfit mb-2">Remove Member?</h3>
              <p className="text-slate-500 text-sm mb-6">
                Are you sure you want to remove{' '}
                <span className="font-semibold text-slate-700">{resolvedName}</span> from the family?
              </p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteStep(0)}
                  className="flex-1 px-4 py-2.5 rounded-2xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors">
                  Cancel
                </button>
                <button
                  data-testid="delete-step1-confirm-btn"
                  onClick={() => setDeleteStep(2)}
                  className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-2xl font-semibold text-sm transition-all">
                  Remove
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Delete Step 2: "Are you absolutely sure?" ───────────────────────── */}
      <AnimatePresence>
        {deleteStep >= 2 && (
          <motion.div
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/25 backdrop-blur-[2px]"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <motion.div
              data-testid="delete-step2-modal"
              className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8"
              initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.18 }}
            >
              <h3 className="text-xl font-bold text-slate-900 font-outfit mb-2">Are you absolutely sure?</h3>
              <p className="text-slate-500 text-sm mb-6">
                Removing{' '}
                <span className="font-semibold text-slate-700">{resolvedName}</span> will revoke all their
                family entitlements and camp access. This cannot be undone.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteStep(1)}
                  className="flex-1 px-4 py-2.5 rounded-2xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors">
                  Go Back
                </button>
                <button
                  data-testid="delete-step2-confirm-btn"
                  onClick={() => setDeleteStep(3)}
                  className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-2xl font-semibold text-sm transition-all">
                  Yes, Remove
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Delete Step 3: Final / Irreversible ─────────────────────────────── */}
      <AnimatePresence>
        {deleteStep >= 3 && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-900/25 backdrop-blur-[2px]"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <motion.div
              data-testid="delete-step3-modal"
              className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8 border-2 border-red-200"
              initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.18 }}
            >
              <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Trash2 size={22} className="text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 font-outfit text-center mb-2">Final Confirmation</h3>
              <p className="text-slate-500 text-sm text-center mb-6">
                This action is{' '}
                <span className="font-semibold text-red-600">irreversible</span>. Please make sure
                you want to permanently remove{' '}
                <span className="font-semibold text-slate-700">{resolvedName}</span>.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteStep(2)}
                  disabled={removing}
                  className="flex-1 px-4 py-2.5 rounded-2xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 disabled:opacity-50 transition-colors">
                  Go Back
                </button>
                <button
                  data-testid="delete-final-confirm-btn"
                  onClick={handleFinalRemove}
                  disabled={removing}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white rounded-2xl font-semibold text-sm transition-all">
                  {removing ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  {removing ? 'Removing…' : 'Remove Forever'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
